import { useState } from 'react';
import { Alert, AlertDescription, Button, cn } from '@design-system';
import { Info, X } from 'lucide-react';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';

const DISMISS_KEY = 'policyModelingMaintenanceBannerDismissed';

function usePolicyModelingMaintenanceBanner() {
  const { flags } = useFeatureFlags();
  const [isDismissed, setIsDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === 'true',
  );

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, 'true');
    setIsDismissed(true);
  };

  return {
    isVisible: Boolean(flags.policyModelingMaintenanceBanner) && !isDismissed,
    dismiss,
  };
}

interface PolicyModelingMaintenanceBannerProps {
  className?: string;
}

export function PolicyModelingMaintenanceBanner({
  className,
}: PolicyModelingMaintenanceBannerProps) {
  const { isVisible, dismiss } = usePolicyModelingMaintenanceBanner();

  if (!isVisible) return null;

  return (
    <Alert variant="info" className={cn('items-start', className)}>
      <Info className="shrink-0" />
      <AlertDescription className="flex items-start justify-between gap-3 text-sm">
        <span>
          We're improving how policy projections are calculated. You may see some values update over
          the coming days as these improvements roll out. This is expected. If a number looks off or
          you have questions, contact support — we're happy to walk through any projection with you.
        </span>
        <Button
          variant="ghost"
          size="sm"
          aria-label="Dismiss notice"
          onClick={dismiss}
          className="-my-1 -mr-1 size-7 shrink-0 p-0"
        >
          <X className="size-4" />
        </Button>
      </AlertDescription>
    </Alert>
  );
}
